
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, Upload, X, ExternalLink, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { 
  loadLyricsFromUrl, 
  loadLyricsFromFile, 
  saveLyricsToStorage, 
  getSongFilename, 
  hasLyrics,
  loadLyricsFromStorage,
  clearLyricsFromStorage
} from '@/utils/lyricsUtils';

export function LyricsManager({ song, isOpen, onClose, onLyricsLoaded, onLyricsCleared }) {
  const [lyricsUrl, setLyricsUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [existingLyrics, setExistingLyrics] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    if (song && isOpen) {
      const lyrics = loadLyricsFromStorage(song);
      setExistingLyrics(lyrics);
      setLyricsUrl(''); 
      setShowClearConfirm(false);
    }
  }, [song, isOpen]);

  const handleUrlLoad = async () => {
    if (!lyricsUrl.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a valid lyrics URL",
        variant: "destructive"
      });
      return;
    }

    if (!song) {
      toast({
        title: "No Song Selected",
        description: "Please select a song first",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const lyricsData = await loadLyricsFromUrl(lyricsUrl);
      saveLyricsToStorage(song, lyricsData);
      setExistingLyrics(lyricsData);
      if (onLyricsLoaded) onLyricsLoaded(lyricsData);
      
      const filename = getSongFilename(song);
      toast({
        title: "Lyrics Loaded!",
        description: `Loaded ${lyricsData.lyrics.length} lyric segments for "${filename}"`,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: "Failed to Load Lyrics",
        description: error.message || "Please check the URL and try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.json')) {
      toast({
        title: "Invalid File Type",
        description: "Please select a JSON file",
        variant: "destructive"
      });
      return;
    }

    if (!song) {
      toast({
        title: "No Song Selected",
        description: "Please select a song first",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const lyricsData = await loadLyricsFromFile(file);
      saveLyricsToStorage(song, lyricsData);
      setExistingLyrics(lyricsData);
      if (onLyricsLoaded) onLyricsLoaded(lyricsData);
      
      const filename = getSongFilename(song);
      toast({
        title: "Lyrics Loaded!",
        description: `Loaded ${lyricsData.lyrics.length} lyric segments for "${filename}"`,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: "Invalid Lyrics File",
        description: error.message || "Please check the file format and try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
    
    event.target.value = '';
  };

  const handleClearLyrics = () => {
    if (!song) return;
    
    const filename = getSongFilename(song);
    const success = clearLyricsFromStorage(song);
    
    if (success) {
      setExistingLyrics(null);
      if (onLyricsCleared) onLyricsCleared(); 
      
      toast({
        title: "Lyrics Cleared",
        description: `Removed lyrics for "${filename}"`,
      });
    } else {
      toast({
        title: "Clear Failed",
        description: `Could not clear lyrics for "${filename}". They might not exist.`,
        variant: "destructive"
      });
    }
    setShowClearConfirm(false);
    // Do not close the manager here, let user see the status or load new ones.
  };

  const filename = getSongFilename(song);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {existingLyrics ? 'Manage Lyrics' : 'Add Lyrics'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {filename && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>Song:</strong> {filename}
                </p>
                <p className="text-xs text-blue-300/80 mt-1">
                  Lyrics will be matched using this filename
                </p>
              </div>
            )}

            {existingLyrics && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-300 font-medium">
                      ✅ Lyrics Already Loaded
                    </p>
                    <p className="text-xs text-green-300/80 mt-1">
                      {existingLyrics.lyrics.length} lyric segments available
                    </p>
                    {existingLyrics.metadata?.generatedAt && (
                      <p className="text-xs text-green-300/60 mt-1">
                        Added: {new Date(existingLyrics.metadata.generatedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowClearConfirm(true)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    title="Clear lyrics"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {showClearConfirm && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-300 font-medium mb-2">
                      Clear Lyrics?
                    </p>
                    <p className="text-xs text-red-300/80 mb-3">
                      This will permanently remove all lyrics data and metadata for this song.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleClearLyrics}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        Clear Lyrics
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowClearConfirm(false)}
                        className="text-white hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {existingLyrics ? 'Replace from URL' : 'Load from URL'}
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://example.com/lyrics.json"
                    value={lyricsUrl}
                    onChange={(e) => setLyricsUrl(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleUrlLoad}
                    disabled={isLoading || !lyricsUrl.trim()}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {existingLyrics ? 'Replace with JSON File' : 'Upload JSON File'}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isLoading}
                  />
                  <Button
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    disabled={isLoading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Lyrics File
                  </Button>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs text-blue-300 mb-2 font-medium">Expected JSON Format:</p>
                <pre className="text-xs text-blue-200 bg-black/20 rounded p-2 overflow-x-auto">
{`{
  "metadata": {
    "generatedAt": "2025-06-17T03:11:32.770Z",
    "sourceFiles": {
      "audio": "${filename || 'song.mp3'}"
    }
  },
  "lyrics": [
    {
      "text": "Lyric line here",
      "startTime": 26,
      "endTime": 30,
      "linePosition": 1
    }
  ]
}`}
                </pre>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
